import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/ui/Button";
import { InputNumber } from "primereact/inputnumber";
import { Tag } from "primereact/tag";
import { ProgressSpinner } from "primereact/progressspinner";
import { Dialog } from "primereact/dialog";
import { useMyPoints, useMyPointsLedger, useRedeemPoints } from "../../../../queries/adMarketplace";

const TYPE_SEVERITY = { earned: "success", redeemed: "warning", expired: "danger", adjusted: "info" };

export default function PointsPanel() {
    const { t }                               = useTranslation();
    const [page,           setPage]           = useState(1);
    const [redeemOpen,     setRedeemOpen]     = useState(false);
    const [redeemAmount,   setRedeemAmount]   = useState(100);

    const { data: pointsData, isLoading: loadingBalance } = useMyPoints();
    const { data: ledgerData, isLoading: loadingLedger  } = useMyPointsLedger(page);
    const redeemMutation = useRedeemPoints();

    const balance = pointsData?.balance ?? 0;
    const ledger  = ledgerData?.data ?? [];
    const meta    = ledgerData?.meta ?? {};

    // ⚠️ THE LEDGER IS PARKED, NOT EMPTY, AND THE DIFFERENCE MATTERS TO A CAFÉ.
    // venue_point_ledger never received a row — creditPoints() threw before
    // writing, on every impression since May — so "no transactions yet" would be
    // a lie that reads as "you have earned nothing". The backend now answers 200
    // with this marker while Phase 1 builds the real impression→credit trail;
    // until then the panel says so plainly instead of showing an empty table.
    const ledgerParked = meta?.message === "ledger_migration_in_progress";

    const handleRedeem = () => {
        redeemMutation.mutate(
            { amount: redeemAmount, notes: "Manual redemption via portal" },
            { onSuccess: () => setRedeemOpen(false) }
        );
    };

    return (
        <div>
            {/* Balance card */}
            <div className="border rounded-xl p-5 bg-gradient-to-br from-blue-600 to-blue-800 text-white mb-6">
                <p className="text-sm opacity-80 mb-1">{t("ad_marketplace.points_balance_title")}</p>
                {loadingBalance ? (
                    <ProgressSpinner style={{ width: "30px", height: "30px" }} strokeWidth="4" />
                ) : (
                    <p className="text-4xl font-bold">{balance.toLocaleString()}</p>
                )}
                <p className="text-xs opacity-70 mt-1">{t("ad_marketplace.points_earned_hint")}</p>
                {/* ⚠️ DISABLED WHILE THE LEDGER IS PARKED. redeemPoints() writes to
                    venue_point_ledger, which is the dead table — and the balance
                    shown is lifetime EARNED with no working debit path, so a
                    redemption would neither be recorded nor reduce anything. A
                    café could press this forever. */}
                <span title={ledgerParked ? t("ad_marketplace.ledger_upgrading_short") : undefined}>
                    <Button
                        label={t("ad_marketplace.redeem_points")}
                        icon="pi pi-gift"
                        className="p-button-outlined p-button-sm mt-3 border-white text-white"
                        style={{ color: "white", borderColor: "white" }}
                        onClick={() => setRedeemOpen(true)}
                        disabled={ledgerParked || balance < 1}
                    />
                </span>
            </div>

            {/* Ledger table */}
            <div>
                <h4 className="font-semibold text-gray-700 mb-3 text-sm">{t("ad_marketplace.transaction_history")}</h4>
                {loadingLedger ? (
                    <div className="flex justify-center py-8"><ProgressSpinner /></div>
                ) : ledgerParked ? (
                    <div className="text-center py-8 px-4 rounded-lg bg-amber-50 border border-amber-200">
                        <i className="pi pi-wrench text-amber-500 text-xl mb-2 block" />
                        <p className="text-sm text-amber-800">{t("ad_marketplace.ledger_upgrading")}</p>
                    </div>
                ) : ledger.length === 0 ? (
                    <p className="text-center text-gray-400 py-8 text-sm">{t("ad_marketplace.no_transactions")}</p>
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b text-xs text-gray-500 uppercase tracking-wide">
                                        <th className="pb-2 text-left">{t("table.date")}</th>
                                        <th className="pb-2 text-left">{t("ad_marketplace.col_campaign")}</th>
                                        <th className="pb-2 text-left">{t("table.type")}</th>
                                        <th className="pb-2 text-right">{t("ad_marketplace.col_amount")}</th>
                                        <th className="pb-2 text-right">{t("ad_marketplace.col_balance")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {ledger.map((entry) => (
                                        <tr key={entry.id} className="border-b last:border-0 hover:bg-gray-50">
                                            <td className="py-2 text-gray-500 text-xs">
                                                {new Date(entry.created_at).toLocaleString([], { dateStyle: "short", timeStyle: "short" })}
                                            </td>
                                            <td className="py-2 text-gray-700 text-xs truncate max-w-32">
                                                {entry.campaign?.name ?? "—"}
                                            </td>
                                            <td className="py-2">
                                                <Tag value={entry.type} severity={TYPE_SEVERITY[entry.type] ?? "info"}
                                                    className="text-xs capitalize" />
                                            </td>
                                            <td className={`py-2 text-right font-mono font-semibold ${
                                                entry.amount >= 0 ? "text-green-600" : "text-red-500"
                                            }`}>
                                                {entry.amount >= 0 ? "+" : ""}{entry.amount}
                                            </td>
                                            <td className="py-2 text-right font-mono text-gray-700">
                                                {entry.balance_after.toLocaleString()}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {meta.last_page > 1 && (
                            <div className="flex justify-center gap-2 mt-4">
                                <Button icon="pi pi-chevron-left" className="p-button-outlined p-button-sm"
                                    disabled={page === 1} onClick={() => setPage(p => p - 1)} />
                                <span className="text-xs self-center text-gray-500">
                                    {t("ad_marketplace.page_of", { current: meta.current_page, total: meta.last_page })}
                                </span>
                                <Button icon="pi pi-chevron-right" className="p-button-outlined p-button-sm"
                                    disabled={page >= meta.last_page} onClick={() => setPage(p => p + 1)} />
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Redeem dialog */}
            <Dialog header={t("ad_marketplace.redeem_dialog_title")} visible={redeemOpen} onHide={() => setRedeemOpen(false)}
                style={{ width: "360px" }} modal>
                <div className="py-2">
                    <p className="text-sm text-gray-600 mb-4">
                        {t("ad_marketplace.redeem_balance_msg", { count: balance.toLocaleString() })}
                    </p>
                    <label className="block text-xs text-gray-500 mb-1">{t("ad_marketplace.redeem_amount_label")}</label>
                    <InputNumber value={redeemAmount} onValueChange={(e) => setRedeemAmount(e.value)}
                        min={1} max={balance} showButtons className="w-full mb-4" />
                    <div className="flex justify-end gap-2">
                        <Button label={t("buttons.cancel")} className="p-button-text p-button-sm" onClick={() => setRedeemOpen(false)} />
                        <Button label={t("ad_marketplace.redeem_points")} icon="pi pi-gift" className="p-button-primary p-button-sm"
                            loading={redeemMutation.isLoading} onClick={handleRedeem} />
                    </div>
                </div>
            </Dialog>
        </div>
    );
}
